import {
  Box,
  Button,
  Card,
  ColorPaletteProp,
  Divider,
  FormControl,
  FormHelperText,
  Input,
  Stack,
  styled,
  Textarea,
  Typography,
} from '@mui/joy';
import { DateTimePicker } from '@mui/x-date-pickers';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form';

import CalendarIconSvg from '@/assets/icons/calendar.svg?react';
import { ErrorAlert } from '@/components/errors/ErrorAlert';
import { ErrorComponent } from '@/components/errors/ErrorComponent';
import { ProcessFormLabel } from '@/components/form-elements/ProcessFormLabel';
import { BaseHeader } from '@/components/navigations/BaseHeader';
import { SignExecutionEdit } from '@/components/processes/SignExecutionEdit';
import { SignStatusAlert } from '@/components/processes/SignStatusAlert';
import { I18N_DE } from '@/i18n/de';
import { UserGuard } from '@/modules/UserGuard';
import { ProcessAction, ProcessImplementationMethod, SignStatusType, UpdateProcessExecutionDto } from '@/modules/api';
import {
  backendApiAuthenticationGetUserOptions,
  backendApiProcessesFindOneExecutionPublicOptions,
  backendApiProcessesUpdateExecutionPublicMutation,
} from '@/modules/api/@tanstack/react-query.gen';
import { zProcessAction } from '@/modules/api/zod.gen';

const CalendarIcon = styled((props: any) => <CalendarIconSvg {...props} />)(() => ({
  width: 24,
  height: 24,
}));

const ACTION: Record<
  ProcessImplementationMethod,
  { completeStatus: SignStatusType; buttonLabel: string; color: ColorPaletteProp }
> = {
  REMOVAL: {
    completeStatus: 'DELETED',
    buttonLabel: `Als ${I18N_DE.SignStatusType.DELETED} markieren`,
    color: 'danger',
  },
  INSTALLATION_OR_APPLICATION: {
    completeStatus: 'PLACED',
    buttonLabel: `Als ${I18N_DE.SignStatusType.PLACED} markieren`,
    color: 'success',
  },
  ADAPTION_OR_MODIFICATION: {
    completeStatus: 'PLACED',
    buttonLabel: `Änderung eintragen`,
    color: 'primary',
  },
};

function RouteComponent() {
  const { processId } = useParams({ strict: false });
  const [pendingAction, setPendingAction] = useState<ProcessAction | null>(null);

  const { data: process } = useSuspenseQuery(
    backendApiProcessesFindOneExecutionPublicOptions({ path: { processId: processId! } }),
  );
  const { data: user } = useSuspenseQuery(backendApiAuthenticationGetUserOptions());

  const methods = useForm<UpdateProcessExecutionDto>({
    defaultValues: { signsExecution: process.signsExecution },
  });
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, dirtyFields },
  } = methods;
  const { fields, append, remove } = useFieldArray({ control, name: 'signsExecution', keyName: 'fieldId' });

  useEffect(() => {
    reset({ signsExecution: process.signsExecution });
  }, [process, reset]);

  const navigate = useNavigate();

  const {
    mutate,
    error: mutationError,
    isPending,
  } = useMutation({
    ...backendApiProcessesUpdateExecutionPublicMutation(),
    onSuccess: (bla, variables) => {
      if (variables.query.action === 'NEXT_STEP') navigate({ to: '/anordnung/success' });
    },
    onSettled: () => setPendingAction(null),
  });

  const completeStatus = process.implementationMethod === 'REMOVAL' ? SignStatusType.DELETED : SignStatusType.PLACED;

  if (process.status !== 'EXECUTION')
    return (
      <ErrorAlert
        error={{ name: 'Fehler', message: `Vorgang befindet sich nicht im Status ${I18N_DE.ProcessStatus.EXECUTION}` }}
      />
    );

  return (
    <FormProvider {...methods}>
      {UserGuard.isSingleSignOnUser(user) && (
        <Box sx={{ width: '100%' }}>
          <BaseHeader />
        </Box>
      )}

      <Stack
        gap={2}
        maxWidth={800}
        p={2}
        component="form"
        onSubmit={handleSubmit(({ signsExecution, executionNotification }, event) => {
          if (isPending) return;

          const parseResult = zProcessAction.safeParse((event?.nativeEvent as SubmitEvent).submitter?.id);
          const action = parseResult.success ? parseResult.data : ProcessAction.SAVE;
          setPendingAction(action);
          mutate({
            path: { processId: processId! },
            query: { action },
            body: {
              executionNotification,
              signsExecution: signsExecution.map(({ images, ...signExecution }) => signExecution),
            },
          });
        })}
      >
        <Typography level="h2">{`Ausführung zur verkehrsrechtlichen Anordnung ${process.sequentialId}`}</Typography>
        <Typography whiteSpace="break-spaces">{process.executionAgency}</Typography>
        <Typography>
          Sie haben diesen Link erhalten um für die Ihnen übermittelte verkehrsrechtliche Anordnung auszuführen.
        </Typography>

        <Divider sx={{ width: '100%' }} />

        {process.signsDraft.map((sign) => {
          const field = fields.find(({ id }) => id === sign.id);
          const fieldIndex = fields.findIndex(({ id }) => id === sign.id);
          const signChange = process.signsChange.find((signChangeDto) => signChangeDto.id === sign.id);
          if (!process.implementationMethod) return null;

          return (
            <Card key={sign.id}>
              <Stack direction="row" alignItems="center" gap={1}>
                <SignStatusAlert status={sign.status} code={sign.code} bearing={sign.group.bearing} />
                <Typography>→</Typography>
                {field ? (
                  <>
                    <SignStatusAlert
                      status={{ type: 'MODIFIED', createdAt: field.status.createdAt }}
                      code={signChange?.code ?? sign.code}
                      bearing={field.bearing}
                    />
                    <Button color="danger" variant="plain" onClick={() => remove(fieldIndex)}>
                      zurücksetzen
                    </Button>
                  </>
                ) : (
                  <>
                    {signChange ? (
                      <SignStatusAlert status={{ type: 'DRAFT', createdAt: '' }} code={signChange.code} />
                    ) : null}
                    <Button
                      key={sign.id}
                      variant="plain"
                      sx={{ paddingLeft: 1, paddingRight: 1 }}
                      color={ACTION[process.implementationMethod].color}
                      onClick={() =>
                        append({
                          id: sign.id,
                          status: { type: completeStatus, createdAt: dayjs().toISOString() },
                          geometry: sign.group.support.geometry,
                          ...(sign.size && { size: sign.size }),
                          ...(sign.reflectionClass && { reflectionClass: sign.reflectionClass }),
                          ...(sign.condition && { condition: sign.condition }),
                          ...(sign.permanency && { permanency: sign.permanency }),
                          ...(sign.value && { value: sign.value }),
                          ...(sign.group.bearing !== undefined &&
                            sign.group.bearing !== null && { bearing: sign.group.bearing }),
                          ...(sign.group.sibId && { sibId: sign.group.sibId }),
                          ...(sign.group.support.type && { supportType: sign.group.support.type }),
                        })
                      }
                    >
                      {ACTION[process.implementationMethod].buttonLabel}
                    </Button>
                  </>
                )}
              </Stack>

              {field && (
                <Stack gap={1} p={1.5} key={field.id}>
                  <Stack direction="row" alignItems="center" gap={1} mb={1}>
                    <FormControl>
                      <ProcessFormLabel>Status</ProcessFormLabel>
                      <Input value={I18N_DE.SignStatusType[field.status.type]} readOnly size="md" />
                    </FormControl>
                    <Controller
                      control={control}
                      name={`signsExecution.${fieldIndex}.status`}
                      rules={{ required: 'Pflichtfeld' }}
                      render={({ field: { value, onChange }, fieldState }) => (
                        <FormControl error={!!fieldState.error} sx={{ flexGrow: 1 }}>
                          <ProcessFormLabel>
                            {field.status.type === 'DELETED' ? 'Entfernungsdatum' : 'Aufstellungsdatum'}
                          </ProcessFormLabel>
                          <DateTimePicker
                            slots={{
                              openPickerIcon: CalendarIcon,
                            }}
                            format="DD.MM.YYYY HH:mm"
                            maxDate={dayjs()}
                            maxTime={dayjs()}
                            value={value ? dayjs(value.createdAt) : null}
                            onChange={(newValue) => {
                              onChange({ ...value, createdAt: newValue?.toISOString() });
                            }}
                            slotProps={{
                              textField: {
                                error: !!fieldState.error,
                              },
                            }}
                          />
                        </FormControl>
                      )}
                    />
                  </Stack>

                  {field.status.type !== 'DELETED' ? <SignExecutionEdit index={fieldIndex} signId={sign.id} /> : null}
                </Stack>
              )}
            </Card>
          );
        })}

        <FormControl error={!!errors.executionNotification}>
          <ProcessFormLabel>Vollzugsmeldung</ProcessFormLabel>
          <Textarea
            variant="outlined"
            sx={{ p: 1 }}
            minRows={4}
            slotProps={{ textarea: { ...register('executionNotification') } }}
            size="md"
          />
          <FormHelperText>{errors.executionNotification?.message}</FormHelperText>
        </FormControl>

        <Stack direction="row" gap={2}>
          <Button
            type="submit"
            id={ProcessAction.SAVE}
            disabled={Object.keys(dirtyFields).length === 0 || isPending}
            loading={pendingAction === ProcessAction.SAVE && isPending}
          >
            speichern
          </Button>
          <Button
            type="submit"
            id={ProcessAction.NEXT_STEP}
            disabled={isPending}
            loading={pendingAction === ProcessAction.NEXT_STEP && isPending}
          >
            abschließen
          </Button>
        </Stack>
        {mutationError && <ErrorAlert error={mutationError} />}
      </Stack>
    </FormProvider>
  );
}

export const Route = createFileRoute('/_authenticated/anordnung/$processId')({
  component: RouteComponent,
  errorComponent: ErrorComponent,
});
